/**
 * Do `value` da tool para as props do cartão — spec-001 do
 * `project-plataforma-por-chat`, [T016] / US-2.
 *
 * ## Por que existe um adaptador, e não seis cartões novos
 *
 * Os seis cartões do fee-fixo são ANTERIORES a esta spec e já têm dono, teste e
 * consumidor na tela (`view-fee-fixo-guia.vue`, `view-fee-fixo-simulacao.vue`,
 * o painel do Especialista). O `chat-message.vue` espalha `ui.props` — que no
 * caminho legado é o `value` cru da tool — direto como props, e o `value` é
 * **snake_case**; três dos cartões ainda esperam o payload EMBRULHADO numa
 * prop só (`filtro`, `pendente`, `preview`). Reescrever os cartões para o fio
 * quebraria os consumidores de tela; reescrever o `apiFeeFixoIa.js` colidiria
 * com o PR #2080, que está aberto sobre ele.
 *
 * Sobra este arquivo: funções PURAS, uma por cartão, que traduzem o `value` nas
 * props que cada um já declara. Quem as chama é o embrulho de
 * `ia/cartoes/*.js`, que é o que a entrada do registry monta.
 *
 * ## O que ele NÃO faz
 *
 * Nenhum número nasce aqui, e nenhuma regra de negócio mora aqui. Onde já
 * existia um normalizador canônico no módulo, ele é IMPORTADO
 * (`normalizarClientesCitados`, `derivarSugestoes`): uma segunda régua de
 * identidade do cliente — ou um segundo "próximo degrau do controle" — seria
 * exatamente o tipo de divergência que só aparece na tela do consultor.
 *
 * Sem `?.`/`??` (o Babel deste projeto não transpila). Acentos UTF-8 reais.
 */
import { normalizarClientesCitados } from '@modules/fee-fixo/api/apiFeeFixoIa'
import {
  ACOES_DO_ITEM_DESFECHO,
  ACOES_DO_ITEM_MIGRACAO,
  OPERACAO_DESFECHO_MIGRACAO,
  OPERACAO_MIGRACAO_EM_LOTE,
} from '@modules/fee-fixo/constants/escritas-ia'
import {
  CONTROLES_DO_RASCUNHO,
  derivarSugestoes,
} from '@modules/fee-fixo/utils/rascunho-simulacao'

/** Objeto de verdade, ou um vazio — nunca `undefined` descendo para o cartão. */
const objeto = (valor) =>
  valor && typeof valor === 'object' && !Array.isArray(valor) ? valor : {}

const lista = (valor) => (Array.isArray(valor) ? valor : [])

const texto = (valor) => (valor ? String(valor) : '')

/**
 * O `S` do motor e as premissas declaradas, nos nomes que o cartão declara.
 *
 * `inputs` e `premissasDeclaradas` são `required` no cartão e chegam do fio
 * como `inputs` / `premissas_declaradas`. O default `{}` não é conveniência: a
 * parte é montada em `input-available` ANTES de o veredito do schema voltar
 * (`chat-message.vue`), e um `undefined` numa prop obrigatória vira warning do
 * Vue — que o `tests/unit/setup.js` transforma em teste vermelho, e que em
 * produção deixaria o `simulate()` desreferenciar `P` dentro do render. Com
 * `{}` quem decide é a guarda de plausibilidade do próprio cartão, que já sabe
 * dizer "o cálculo não fechou".
 */
export const propsDaSimulacao = (value) => {
  const v = objeto(value)
  return {
    inputs: objeto(v.inputs),
    premissasDeclaradas: objeto(v.premissas_declaradas),
    // Vazio (e não `null`) quando ausente: o cartão testa a presença para
    // decidir se oferece o Salvar, e um só tipo falsy evita o `=== null` que
    // erra com `undefined`. Mesma régua do captador.
    previewId: texto(v.preview_id),
    // O commit da simulação é do dono da tela (o depósito de `resultados`
    // acontece no navegador, antes da frase de confirmação). No thread não há
    // gravação em voo para reportar.
    processando: false,
    // G-22 rodada 2: o aviso do backend quando a simulação usou carteira mais
    // antiga que um upload pendente — mesma régua do captador legado
    // (`api/apiFeeFixoIa.js::_capturarSimulacao`), para as duas portas do
    // mesmo cartão não divergirem no dia em que o campo mudar de nome.
    avisoCarteiraPendente: texto(v.aviso_carteira_pendente),
  }
}

/**
 * O recorte, na prop única que o cartão declara.
 *
 * Espelha `normalizarFiltroSugerido` (`api/apiFeeFixoIa.js`), que não é
 * exportado: importar a classe do chat aqui traria o motor SSE para dentro de
 * um adaptador puro. As três chaves são as mesmas, com os mesmos defaults; se
 * um dia divergirem, o cartão mostra recorte diferente do que a aba Clientes
 * aplica, e o teste que pega isso é o desta pasta.
 */
export const propsDoRecorte = (value) => {
  const v = objeto(value)
  return {
    filtro: {
      valido: !!v.valido,
      filtros: lista(v.filtros),
      erros: lista(v.erros),
    },
  }
}

/**
 * Os clientes citados, no shape que `abrirConversaDeCliente` consome.
 *
 * `normalizarClientesCitados` é IMPORTADO de propósito: é ele que lê as duas
 * formas do payload (`cliente` e `clientes[]`), descarta quem não tem
 * `id_cliente_app` (sem id não há atalho — o código sozinho é ambíguo) e
 * ignora o `requer_escolha`, que é ambiguidade e não vira atalho.
 */
export const propsDoAtalhoDeCliente = (value) => {
  const v = objeto(value)
  return {
    clientes: normalizarClientesCitados(v),
    // Vazio é caso legítimo: o cartão abre a conversa, só não semeia o
    // compositor (spec-006, US-3).
    assunto: texto(v.assunto),
  }
}

// As ações válidas por OPERAÇÃO — a peneira do captador
// (`ACOES_DO_ITEM_POR_TOOL`), reindexada pelo que o `value` carrega. Ação fora
// da lista não atravessa: um `ja_no_funil` chegando no card do desfecho
// acenderia ali o aviso "vou pular", que não existe nessa escrita — ela FECHA
// uma conversa, não pula nenhuma.
const ACOES_POR_OPERACAO = {
  [OPERACAO_MIGRACAO_EM_LOTE]: ACOES_DO_ITEM_MIGRACAO,
  [OPERACAO_DESFECHO_MIGRACAO]: ACOES_DO_ITEM_DESFECHO,
}

const itemDoPreview = (item, acoesPermitidas) => {
  const bruto = objeto(item)
  const acao = texto(bruto.acao)
  return {
    codigo: texto(bruto.codigo),
    // `nome` chega nulo quando o cadastro não tem nome — é o caso comum na
    // base importada, e quem resolve a exibição é o cartão (`nomeUtil`).
    nome: texto(bruto.nome),
    acao: acoesPermitidas.indexOf(acao) >= 0 ? acao : '',
  }
}

/**
 * O preview da escrita, na prop única que o cartão declara.
 *
 * Um cartão, duas escritas (lote e desfecho): o que muda é a copy, e ela sai
 * da `operacao` que vem no próprio `value`.
 */
export const propsDaConfirmacao = (value) => {
  const v = objeto(value)
  const operacao = texto(v.operacao)
  const acoesPermitidas = ACOES_POR_OPERACAO[operacao] || []
  return {
    pendente: {
      operacao: operacao,
      // Do motor (quantos entram, quantos são pulados). A tela não reescreve
      // nem recalcula.
      resumo: texto(v.resumo),
      // O id que AUTORIZA o commit. Ausente, o cartão esconde o Confirmar —
      // o servidor recusaria a gravação.
      previewId: texto(v.preview_id),
      aviso: texto(v.aviso),
      itens: lista(v.itens).map((item) => itemDoPreview(item, acoesPermitidas)),
    },
    // A segunda chamada (a que grava) é disparada pela view, não pelo thread.
    processando: false,
  }
}

/**
 * O ESTADO do `value` (`ui.props.estado`) → o `resolvido` que o cartão lê —
 * espelho de `IaConfirmAction`/`cartao-campos-reuniao` (`'confirmed'
 * |'cancelled'`), só que com o vocabulário PRÓPRIO deste cartão
 * (`'enviado'|'erro'|'cancelado'`), que já existia ANTES da tool virar
 * two-step. `'descartado'` (o nome do backend, `preview_pendente.
 * DECISAO_DESCARTE`) vira `'cancelado'` (o nome que o TEMPLATE já lê,
 * `resolvido === 'cancelado'`) — renomear o template pra bater com o backend
 * quebraria o consumidor mais antigo (o painel do Especialista, que ainda
 * orquestra a chamada local e escreve `'cancelado'` direto, ver abaixo).
 */
// ★ [G-4, rodada-v5, 09/09/2026] EXPORTADO — `fee-fixo-ia-envio-relatorio-
// desfecho-externo.js` (irmão do `.vue`) importa este MESMO mapa para
// traduzir o `estado` que OUTRA mensagem já publicou para o `previewId`
// deste cartão. Uma segunda cópia divergiria no dia em que o vocabulário
// mudasse — é a única linha não-trivial do mapa (`'descartado' →
// 'cancelado'`) que faria o cartão de origem mostrar um rótulo diferente do
// cartão de desfecho para o MESMO evento.
export const RESOLVIDO_POR_ESTADO = {
  enviado: 'enviado',
  erro: 'erro',
  descartado: 'cancelado',
}

/**
 * O preview do envio — o cartão lê o payload CRU (snake_case) de dentro da
 * prop `preview`, então aqui só o embrulho é preciso.
 *
 * ★ [rodada-v5, 09/09/2026] `preparar_envio_relatorio_fee_fixo` virou escrita
 * two-step com commit no SERVIDOR (antes era leitura pura, ADR-001 da
 * spec-005) — o `value` ganhou QUATRO formas (`ui-fee-fixo-envio-
 * relatorio.schema.json`, `anyOf`): PREVIEW (sem `estado`), ENVIADO, ERRO e
 * DESCARTE. As três últimas chegam pela MESMA tool_call que o preview, então
 * `resolvido`/`mensagemErro`/`resultado`/`previewId` agora também podem vir
 * do PRÓPRIO payload — mapeados aqui, e não mais só do painel que orquestra a
 * chamada local (`fee-fixo-ia-especialista.vue`, que monta este componente
 * DIRETO, fora do registry, e continua escrevendo essas mesmas props a partir
 * do PRÓPRIO ciclo de vida da requisição — os dois caminhos convergem no
 * mesmo vocabulário do componente, nunca um terceiro). Sem `estado` no
 * payload (a forma PREVIEW), `resolvido` sai `''` — o default do cartão.
 */
export const propsDoEnvioDeRelatorio = (value) => {
  const v = objeto(value)
  return {
    preview: v,
    resolvido: RESOLVIDO_POR_ESTADO[v.estado] || '',
    mensagemErro: texto(v.mensagem_erro),
    resultado: v.resultado && typeof v.resultado === 'object' ? v.resultado : null,
    previewId: texto(v.preview_id),
  }
}

// Escreve `valor` no caminho `a.b` de um objeto, criando o que faltar. Só
// existe porque `CONTROLES_DO_RASCUNHO` endereça o estado do laboratório por
// caminho (`P.fee_prof`), e é dele que sai a lista de campos abaixo.
const escreverNoCaminho = (alvo, caminho, valor) => {
  const partes = String(caminho).split('.')
  const ultima = partes.pop()
  const no = partes.reduce((atual, chave) => {
    if (!atual[chave]) atual[chave] = {}
    return atual[chave]
  }, alvo)
  no[ultima] = valor
}

/**
 * O estado do laboratório reconstruído a partir das PREMISSAS ecoadas no
 * payload.
 *
 * ★ É a diferença entre o cartão na tela e o cartão no thread. Na tela,
 * `derivarSugestoes` recebe o `state` vivo do Fee Lab — que aqui não existe,
 * porque a conversa pode estar aberta em qualquer tela. Mas o `value` da tool
 * ECOA os valores que a tela mandou (`premissas.horizonte_anos`,
 * `anos_migracao`, `fee_profissional_bps`), com a unidade no nome; é deles que
 * o valor atual sai. Nenhum número é inventado aqui, e o "próximo degrau"
 * continua sendo o do controle, calculado pela MESMA função da tela.
 *
 * O mapa é derivado de `CONTROLES_DO_RASCUNHO`: controle novo cujo nome de
 * premissa case com o payload passa a funcionar sem tocar neste arquivo.
 */
const estadoDoRascunho = (premissas) => {
  const p = objeto(premissas)
  const estado = {}
  Object.keys(CONTROLES_DO_RASCUNHO).forEach((chave) => {
    const valor = p[chave]
    if (typeof valor !== 'number' || !Number.isFinite(valor)) return
    escreverNoCaminho(estado, CONTROLES_DO_RASCUNHO[chave].path, valor)
  })
  return estado
}

/**
 * Os ajustes sugeridos, derivados como a tela os deriva.
 *
 * ⚠️ `sugestoes`, `aplicadas` e `desfavoravel` NÃO estão no payload — o
 * contrato do backend (`ui-fee-fixo-sugestao-rascunho.schema.json`) registra
 * isso explicitamente. `aplicadas` é estado do CLIQUE do consultor e nasce
 * vazia aqui: aplicar um ajuste move um controle do laboratório, e no thread o
 * laboratório pode nem estar aberto — quem aplica é a tela.
 */
export const propsDaSugestaoDeRascunho = (value) => {
  const v = objeto(value)
  const leitura = objeto(v.leitura)
  return {
    sugestoes: derivarSugestoes({
      oQueMudaria: lista(v.o_que_mudaria),
      state: estadoDoRascunho(v.premissas),
    }),
    aplicadas: [],
    // `null` no payload é "desconhecido", nunca "não" — e o cartão só muda a
    // frase de abertura, então o falso é o default seguro.
    desfavoravel: !!leitura.cenario_desfavoravel_para_o_cliente,
  }
}

/**
 * O DESFECHO da gravação (G-14) — `fee-fixo/simulacao-gravada`.
 *
 * O fio é snake_case; o cartão declara props camelCase. Nenhum cálculo aqui:
 * o commit já resolveu identidade e completude, e recalcular criaria a segunda
 * fonte do mesmo fato.
 *
 * ★ `id_cliente_app` NULO passa como `null`, não vira `0` nem `''`: é ele que
 * o cartão usa para decidir se desenha o CTA, e um zero ali ligaria um botão
 * que não abre cliente nenhum.
 */
export const propsDoCenarioGravado = (value) => {
  const v = objeto(value)
  const id = v.id_cliente_app
  return {
    idAnalise: texto(v.id_analise),
    idClienteApp: id === undefined || id === null ? null : id,
    codigoCliente: texto(v.codigo_cliente),
    nomeCliente: texto(v.nome_cliente),
    cenarioCompleto: Boolean(v.cenario_completo),
  }
}

/**
 * O checklist da proposta R2 — `fee-fixo/checklist-r2` (spec-010-r2-guiada-
 * pelo-agente-com-transcricao, [T006], US-2/US-4; 8º item `racional_discutido`
 * e `proximo_passo.opcoes` entraram na spec-006-esteira-migracao-fee-fixo,
 * [T004]/[T011]).
 *
 * O fio é snake_case (`nome_cliente`, `proximo_passo`); o cartão declara
 * `nomeCliente`/`proximoPasso` em camelCase. Os 8 itens e o `proximo_passo`
 * já vêm PRONTOS do backend (`proposta_r2_checklist.montar_checklist`), na
 * ordem canônica — este adaptador só traduz o envelope, nunca reordena nem
 * recalcula nada.
 *
 * ⚠️ `opcoes` é campo ADITIVO de `proximo_passo` (só existe quando
 * `chave === 'escolher_entrega'`, `contracts/api.md §2` da spec-006) — sem
 * repassá-lo aqui, a esteira chegava ao fim do checklist e o cartão perdia
 * silenciosamente as duas entregas que o backend ofereceu.
 */
export const propsDoChecklistR2 = (value) => {
  const v = objeto(value)
  const passo = objeto(v.proximo_passo)
  const proximoPasso = {
    chave: texto(passo.chave),
    rotulo: texto(passo.rotulo),
    pedido: texto(passo.pedido),
  }
  if (Array.isArray(passo.opcoes)) {
    proximoPasso.opcoes = passo.opcoes.map((opcao) => {
      const o = objeto(opcao)
      return {
        chave: texto(o.chave),
        rotulo: texto(o.rotulo),
        tool: texto(o.tool),
      }
    })
  }
  return {
    nomeCliente: texto(v.nome_cliente),
    itens: lista(v.itens),
    proximoPasso: proximoPasso,
    pendentes: typeof v.pendentes === 'number' ? v.pendentes : 0,
  }
}

/**
 * Os campos da reunião — `fee-fixo/campos-reuniao` (spec-010-r2-guiada-pelo-
 * agente-com-transcricao, G-16 do QA it.5, complemento do [T006]).
 *
 * UM schema (`ui-fee-fixo-campos-reuniao.schema.json`), TRÊS estados por
 * `anyOf`: o PREVIEW (`preview_id`/`resumo`/`campos`/`total_identificados`),
 * o DESFECHO (`gravados`/`nao_gravados`), depois do `confirmar=true`
 * interceptado no servidor (G-13), e o DESCARTE (`descartado: true`, G-18) —
 * o preview foi recusado (botão Descartar ou fala fora do vocabulário de
 * confirmação) e nada foi gravado. `id_cliente_app`/`cliente` são comuns aos
 * três — mesmo padrão de `ui-fee-fixo-cliente-atalho-card`.
 *
 * Nada é recalculado aqui: o backend já resolveu identidade, o rótulo PT-BR
 * de cada campo (`tool_popular_campos_reuniao_cartao.rotulo_do_campo`) e o
 * motivo de cada recusa — este adaptador só traduz snake_case → camelCase.
 */
export const propsDoCartaoCamposReuniao = (value) => {
  const v = objeto(value)
  const cliente = objeto(v.cliente)
  const id = v.id_cliente_app
  return {
    idClienteApp: id === undefined || id === null ? null : id,
    clienteCodigo: texto(cliente.codigo),
    clienteNome: texto(cliente.nome),
    previewId: texto(v.preview_id),
    resumo: texto(v.resumo),
    campos: lista(v.campos),
    totalIdentificados:
      typeof v.total_identificados === 'number' ? v.total_identificados : 0,
    gravados: objeto(v.gravados),
    naoGravados: lista(v.nao_gravados),
    // [G-18, spec-010 QA it.6] `descartado` só existe no estado DESCARTE do
    // schema (`anyOf` com `required: ['descartado']`) — `Boolean(undefined)`
    // é `false` nos outros dois estados, sem precisar de um terceiro `if`.
    descartado: Boolean(v.descartado),
  }
}

/**
 * O protocolo do deck da proposta R2 — `fee-fixo/deck-r2-protocolo` (spec-010-
 * r2-guiada-pelo-agente-com-transcricao, [T010]/[T033], complemento da [T006]).
 *
 * A tool `gerar_proposta_r2` (two-step) emite este MESMO `ui.component` nas
 * duas etapas — `preview` (sem side-effect) e `protocolo` (depois do
 * `confirmar=true`) — e é o campo `etapa` que separa as duas leituras dentro
 * do cartão único. `preview_id` só vem na primeira; `hash`/`pdf_type`/
 * `caminho_status` só na segunda — os quatro chegam `undefined` no ramo em que
 * não existem, e o `texto()` os reduz a `''` sem o cartão precisar saber qual
 * etapa está lendo para não quebrar.
 */
export const propsDoDeckR2Protocolo = (value) => {
  const v = objeto(value)
  return {
    nomeCliente: texto(v.nome_cliente),
    etapa: texto(v.etapa),
    folhasOmitidas: lista(v.folhas_omitidas),
    degradado: Boolean(v.degradado),
    previewId: texto(v.preview_id),
    hash: texto(v.hash),
    pdfType: texto(v.pdf_type),
    caminhoStatus: texto(v.caminho_status),
  }
}

/**
 * A planilha exportada — `fee-fixo/exportar-planilha` (spec-018, [T008], US-2
 * / Q5 do roteiro).
 *
 * ★ Ligado ao `UI_REGISTRY` desde o [T008b] (backend, 09/09/2026):
 * `exportar_planilha` emite `ui = cartao('fee-fixo/exportar-planilha', props)`
 * (`core/ui_cartao.py`) no ramo com arquivo, e `contratos/tools-publicas.json`
 * hoje diz `"label": "Montando a planilha...", "tem_ui": true`.
 *
 * Esta função traduz o `value` da tool — `fonte`/`top_n_efetivo`/
 * `total_elegiveis`/`total_na_base`/`momento_apuracao`/
 * `arquivo{base64,name,extension}` — e o `ui.props` espelha exatamente esses
 * campos, de propósito: os dois caminhos (legado por `value`, novo por `ui`)
 * desenham o MESMO cartão, sem vocabulário diferente por caminho.
 *
 * `total_elegiveis`/`total_na_base` preservam `null` (nunca viram 0): a tool
 * só os omite quando a fonte não os apurou, e um cartão que imprimisse "Top 10
 * de 0 elegíveis" ali mentiria uma base vazia que não existe.
 */
/**
 * `fee-fixo/apresentacao` (spec-021 [T007]) — o andamento do documento que
 * `gerar_apresentacao` disparou. `protocolo` vem como STRING de propósito: o
 * id é UUID desde a migration v480, e `type: Number` no componente de
 * progresso já produziu "Expected Number with value NaN" em produção.
 */
export const propsDoCartaoApresentacao = (value) => {
  const v = objeto(value)
  return {
    protocolo: texto(v.protocolo),
    motor: texto(v.motor),
    etapa: texto(v.etapa),
    formato: texto(v.formato),
    nomeCliente: texto(v.nome_cliente),
  }
}

export const propsDoCartaoExportarPlanilha = (value) => {
  const v = objeto(value)
  const arquivo = objeto(v.arquivo)
  return {
    fonte: texto(v.fonte),
    topNEfetivo: typeof v.top_n_efetivo === 'number' ? v.top_n_efetivo : 0,
    totalElegiveis:
      typeof v.total_elegiveis === 'number' ? v.total_elegiveis : null,
    totalNaBase: typeof v.total_na_base === 'number' ? v.total_na_base : null,
    momentoApuracao: texto(v.momento_apuracao),
    arquivo: {
      base64: texto(arquivo.base64),
      name: texto(arquivo.name),
      extension: texto(arquivo.extension) || 'xlsx',
    },
  }
}
